import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import HeaderActions from './HeaderActions';
import MobileBottomNav from './MobileBottomNav';
import MobileHeader from './MobileHeader';
import MiniPlayer from '../MiniPlayer/MiniPlayer';
import CircuitRunBanner from '../Flow/CircuitRunBanner';
import TimerEngine from '../TimerEngine/TimerEngine';
import AudioEngine from '../AudioEngine/AudioEngine';
import GamificationOverlay from '../GamificationOverlay';
import XPToast from '../XPToast/XPToast';
import CommandPalette from '../CommandPalette';
import QuickCapture from './QuickCapture';
import ModalManager from '../ModalManager/ModalManager';
import VoiceDumpWizard from '../VoiceDumpWizard/VoiceDumpWizard';
import AICoachWidget from '../AICoachWidget/AICoachWidget';
import CreditWarningToast from '../CreditWarningToast/CreditWarningToast';
import UpdateNotificationBar from '../UpdateNotificationBar/UpdateNotificationBar';
import FrictionDetector from '../FrictionDetector/FrictionDetector';
import RitualTriggerOverlay from '../Rituals/RitualTriggerOverlay';
import { PageContextProvider } from '../../contexts/PageContextProvider';
import { WorkLifeFilterProvider, useWorkLifeFilter } from '../../contexts/WorkLifeFilterContext';
import useThemeStore from '../../stores/useThemeStore';
import { needsTrafficLightInset, usesCustomWindowControls } from '../../utils/desktopWindow';
import useUserStore from '../../stores/useUserStore';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';
import useContextStore from '../../stores/useContextStore';
import { storage } from '../../utils/storage';
import './AppShell.css';

/** The persisted sidebar preference. Read fresh every time — never captured. */
function readCollapsePref() {
    try { return storage.get('sidebar_collapsed', false); } catch { return false; }
}

const WORK_LIFE_KEY = 'work_life_filter';

/**
 * With the `contextFilter` module OFF the app must behave as if context
 * filtering did not exist. Hiding the control is not enough — BOTH filters are
 * persisted, and their consumers read the store/context directly:
 *   · work_life_filter → Dashboard, Areas (via useWorkLifeFilter)
 *   · active_context   → Today, AllTasks, Projects, Dashboard (via
 *                        useContextFilter); its UI is ContextSwitcher, mounted
 *                        in the Header beside the Work/Life toggle behind this
 *                        same module gate.
 * Left alone, a user who turns the module off after picking "Work" keeps a
 * filter they can no longer see or clear, and half their data stays hidden.
 * Both are therefore forced back to "everything" while the module is off. Only
 * filters are reset — no user data is touched.
 *
 * Runs BEFORE WorkLifeFilterProvider's useState initialiser (a parent's body
 * executes before its children render), so the very first paint is unfiltered
 * rather than filtered-then-corrected. Idempotent: the writes are guarded.
 */
function normalizeContextFilters(enabled) {
    if (enabled) return;
    try {
        if (storage.get(WORK_LIFE_KEY, 'all') !== 'all') storage.set(WORK_LIFE_KEY, 'all');
    } catch { /* filter reset is best-effort */ }
    if (useContextStore.getState().hasContext()) useContextStore.getState().clearContext();
}

/**
 * Keeps the guarantee true for the rest of the session: WorkLifeFilterProvider
 * only reads storage once, at mount, so turning the module off mid-session needs
 * the React state pushed back to 'all' too (setFilter also rewrites storage).
 */
function ContextFilterGate({ enabled }) {
    const { filter, setFilter } = useWorkLifeFilter();
    useEffect(() => {
        if (!enabled && filter !== 'all') setFilter('all');
    }, [enabled, filter, setFilter]);
    return null;
}

export default function AppShell() {
    const theme = useThemeStore((state) => state.theme);
    const focusAudioEnabled = useUserStore((s) => s.preferences.focusAudioEnabled);

    // ─── Context Filters module (default OFF) ───
    const contextFilterEnabled = useUserStore((s) => s.isModuleEnabled('contextFilter'));
    normalizeContextFilters(contextFilterEnabled);

    // Lifted mobile drawer state — shared between Sidebar and MobileBottomNav
    const [mobileOpen, setMobileOpen] = useState(false);

    // ─── Sidebar collapse — AppShell is the SOLE owner ───
    // Three writers used to race over this flag (Sidebar's own toggle, its
    // session effect, its tablet media query), two of them skipping persistence
    // and one reading a stale `collapsed` from a closure. Everything now funnels
    // through here, and Sidebar receives the result as a prop.
    //
    // `sidebar_collapsed` in storage is the USER PREFERENCE. Session Zen and the
    // tablet breakpoint force the rail without overwriting it, then restore it —
    // and they read it back from storage rather than capturing it, so there is
    // no stale closure left to go wrong.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readCollapsePref());

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            try { storage.set('sidebar_collapsed', next); } catch { /* preference is best-effort */ }
            return next;
        });
    }, []);

    // Focus fullscreen: hide mobile nav during active session
    const isTimerRunning = useTimerStore(s => s.isRunning);
    const flowExecutionState = useFlowStore(s => s.executionState);
    const isSessionActive = isTimerRunning || flowExecutionState === 'running';

    // Read by the (dependency-free) breakpoint effect below so a resize can never
    // un-rail an active session. Written by the session effect, which is declared
    // first and therefore runs first on mount.
    const sessionActiveRef = useRef(isSessionActive);

    // Auto-Zen: rail the sidebar for the duration of a session, then restore the
    // preference (including any change the user made mid-session).
    //
    // The breakpoint check is NOT redundant: the effect below only fires at mount
    // and on a matchMedia change, so ending a session at, say, 900px would
    // otherwise expand the sidebar on a tablet — against the documented
    // max-width:1024px auto-collapse. Both writers apply the same rule.
    useEffect(() => {
        sessionActiveRef.current = isSessionActive;
        setSidebarCollapsed(
            isSessionActive || window.matchMedia('(max-width: 1024px)').matches
                ? true
                : readCollapsePref()
        );
    }, [isSessionActive]);

    // Tablet: rail below 1024px. The `else` branch is the bug fix — crossing back
    // above the breakpoint used to leave the sidebar railed forever, despite a
    // comment claiming it restored.
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1024px)');
        const apply = (matches) => setSidebarCollapsed(
            matches || sessionActiveRef.current ? true : readCollapsePref()
        );
        apply(mq.matches);
        const handleChange = (e) => apply(e.matches);
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    // Apply explicit theme to document (resolving 'system' to actual mode) so PostHog records it
    useEffect(() => {
        const root = document.documentElement;
        let activeTheme = theme;
        
        if (theme === 'system') {
            activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        
        root.setAttribute('data-theme', activeTheme);
        
        // Listeners for system changes when in system mode
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            if (useThemeStore.getState().theme === 'system') {
                root.setAttribute('data-theme', e.matches ? 'dark' : 'light');
            }
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // ─── Which title bar are we living under? ───
    // 'custom' — Windows/Linux desktop: decorations are stripped in Rust and the
    //            Header is the drag strip, with our own caption buttons.
    // 'macos'  — real traffic lights float over the content (titleBarStyle
    //            "Overlay"), so the sidebar's top strip has to leave room.
    // absent   — the web build, where the browser owns the frame.
    // Stamped on <html> rather than threaded through props: the inset is a
    // window fact, and both the Sidebar and the Header need to read it.
    useEffect(() => {
        const root = document.documentElement;
        if (needsTrafficLightInset()) root.dataset.titlebar = 'macos';
        else if (usesCustomWindowControls()) root.dataset.titlebar = 'custom';
        else delete root.dataset.titlebar;
    }, []);

    return (
        <WorkLifeFilterProvider>
        <ContextFilterGate enabled={contextFilterEnabled} />
        <PageContextProvider>
            <UpdateNotificationBar />
            <div className={`app-shell ${isSessionActive ? 'app-shell--focus-active' : ''}`}>
                <Sidebar
                    mobileOpen={mobileOpen}
                    setMobileOpen={setMobileOpen}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebar}
                />
                <div className="app-shell__main-area">
                    {/* Collapse lives on the sidebar's own RailToggle (decision 6).
                        `hidden` empties the bar for the duration of a session —
                        Header then renders null, so the flex column reserves no
                        row for it (the same Zen contract MobileHeader uses). */}
                    <Header hidden={isSessionActive} />
                    {/* Same two utility buttons as desktop Header, through the
                        `actions` slot MobileHeader has always had — it just
                        never received anything before (Header is display:none
                        below 767px, so a phone had no way to reach Inbox or
                        the AI Coach widget at all). */}
                    <MobileHeader
                        onHamburgerPress={() => setMobileOpen(true)}
                        hidden={isSessionActive}
                        actions={<HeaderActions variant="mobile" />}
                    />
                    <CircuitRunBanner />
                    <main className="app-shell__content">
                        <Outlet />
                    </main>
                </div>
                <MobileBottomNav
                    onMorePress={() => setMobileOpen(true)}
                    hidden={isSessionActive}
                />
                <TimerEngine />
                {focusAudioEnabled && <AudioEngine />}
                <MiniPlayer />
                <GamificationOverlay />
                <XPToast />
                <CommandPalette />
                <QuickCapture />
                <ModalManager />
                <VoiceDumpWizard />
                <AICoachWidget />
                <CreditWarningToast />
                <FrictionDetector />
                <RitualTriggerOverlay />
            </div>
        </PageContextProvider>
        </WorkLifeFilterProvider>
    );
}
